/* eslint-disable sonarjs/no-duplicate-string */
import React, { lazy } from 'react';
import { Redirect } from 'react-router-dom';

// Layouts
import EmptyLayout from 'layouts/EmptyLayout';

// Containers
import SellerGuard from '../SellerGuard';

// Pages
const SellerFinancialPage = lazy(() => import('pages/sellers/SellerFinancialPage'));

// Public entries (no panel chrome) — mounted directly by ./index.js
export const sellerRegisterRoute = {
  exact: true,
  path: '/sellers/register',
  layout: EmptyLayout,
  component: lazy(() => import('pages/sellers/SellerRegistrationPage')),
};

export const sellerChooseRoute = {
  path: '/sellers',
  layout: EmptyLayout,
  guard: SellerGuard,
  component: lazy(() => import('pages/sellers/SellerChoosePage')),
};

// Panel children — mounted under the shared AppLayout route by ./index.js
export const sellerPanelRoutes = [
  {
    exact: true,
    path: '/sellers/:sellerSlug/customers',
    component: lazy(() => import('pages/sellers/SellerCustomerPage')),
    permissionKey: 'cx.customers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/customers/:customerUid',
    component: lazy(() => import('pages/sellers/SellerCustomerDetailPage')),
    permissionKey: 'cx.customers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/partnerships/new',
    permissionKey: 'partnerships.partnerships',
    component: ({ match, location }) => {
      const params = new URLSearchParams(location.search);
      params.set('setup_guiado', '1');
      return (
        <Redirect
          to={{
            pathname: `/sellers/${match.params.sellerSlug}/partnerships`,
            search: `?${params}`,
            hash: location.hash,
          }}
        />
      );
    },
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/partnerships/:partnershipInstitutionSlug',
    component: lazy(() => import('pages/sellers/SellerPartnershipDetailsPage')),
    permissionKey: 'partnerships.partnerships',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/partnerships',
    component: lazy(() => import('pages/sellers/SellerPartnershipPage')),
    permissionKey: 'partnerships.partnerships',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/brands',
    component: lazy(() => import('pages/sellers/SellerBrandPage')),
    permissionKey: 'catalog.brands',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/product-classes',
    component: lazy(() => import('pages/sellers/SellerProductClassesPage')),
    permissionKey: 'catalog.product_classes',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/product-classes/:productClassSlug',
    component: lazy(() => import('pages/sellers/SellerProductClassesDetailsPage')),
    permissionKey: 'catalog.product_classes',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/income-report/:year',
    component: lazy(() => import('pages/sellers/SellerFinancialIncomeReportPage')),
    permissionKey: 'financial.statement',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/arrangements',
    component: lazy(() => import('pages/sellers/SellerFinancialArrangementsPage')),
    permissionKey: 'financial.commissions',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/indicators',
    component: lazy(() => import('pages/sellers/SellerFinancialIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/sales-agreements-commission-accounts/:commissionAccountUid',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales-agreements/commission-accounts/${match.params.commissionAccountUid}`,
          search: location.search,
        }}
      />
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/sales-agreements-commission-accounts',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales-agreements/commission-accounts`,
          search: location.search,
        }}
      />
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial/:withdrawalUid',
    component: lazy(() => import('pages/sellers/SellerFinancialWithdrawalPage')),
    permissionKey: 'financial.statement',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/financial',
    component: props => {
      const { match, location } = props;
      const searchParams = new URLSearchParams(location.search);

      if (searchParams.get('tab') !== 'invoices') return <SellerFinancialPage {...props} />;

      searchParams.delete('tab');

      return (
        <Redirect
          to={{
            pathname: `/sellers/${match.params.sellerSlug}/tax-invoices/commission-approvals`,
            search: searchParams.toString(),
          }}
        />
      );
    },
    permissionKey: 'financial.statement',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/overview',
    component: lazy(() => import('pages/sellers/SellerPaymentOverviewPage')),
    permissionKey: 'payments.payments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/indicators',
    component: lazy(() => import('pages/sellers/SellerPaymentIndicatorsPage')),
    permissionKey: 'payments.payments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/refunds',
    component: lazy(() => import('pages/sellers/SellerPaymentRefundsPage')),
    permissionKey: 'payments.payments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/chargebacks/:chargebackCode',
    component: lazy(() => import('pages/sellers/SellerPaymentChargebackDetailsPage')),
    permissionKey: 'payments.chargebacks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/chargebacks',
    component: lazy(() => import('pages/sellers/SellerPaymentChargebacksPage')),
    permissionKey: 'payments.chargebacks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments/reembolsos',
    component: ({ match, location }) => (
      <Redirect
        to={`/sellers/${match.params.sellerSlug}/payments/refunds${location.search || ''}`}
      />
    ),
    permissionKey: 'payments.payments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/payments',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/payments/overview`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'payments.payments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/chargebacks/:chargebackCode',
    component: ({ match }) => (
      <Redirect
        to={`/sellers/${match.params.sellerSlug}/payments/chargebacks/${match.params.chargebackCode}`}
      />
    ),
    permissionKey: 'payments.chargebacks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/chargebacks',
    component: ({ match, location }) => (
      <Redirect
        to={`/sellers/${match.params.sellerSlug}/payments/chargebacks${location.search || ''}`}
      />
    ),
    permissionKey: 'payments.chargebacks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/logistics',
    component: lazy(() => import('pages/sellers/SellerLogisticPage')),
    permissionKey: 'logistics.warehouses',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing-groups',
    component: lazy(() => import('pages/sellers/SellerBillingGroupsPage')),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/institution-shipment',
    component: lazy(() => import('pages/sellers/SellerInstitutionShipmentPage')),
    permissionKey: 'logistics.institution_shipments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/institution-shipment/:shipmentCode',
    component: lazy(() => import('pages/sellers/SellerInstitutionShipmentDetailsPage')),
    permissionKey: 'logistics.institution_shipments',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/prices',
    component: lazy(() => import('pages/sellers/SellerPricePage')),
    permissionKey: 'pricing.prices',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/stock-alerts',
    component: lazy(() => import('pages/sellers/SellerStockAlertPage')),
    permissionKey: 'logistics.stock_alerts',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/product-alerts',
    component: lazy(() => import('pages/sellers/SellerProductAlertPage')),
    permissionKey: 'catalog.product_alerts',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products/:productSlug/variants/:variantSlug',
    component: lazy(() => import('pages/sellers/SellerProductVariantEditPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products/:productSlug',
    component: lazy(() => import('pages/sellers/SellerProductEditPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products',
    component: lazy(() => import('pages/sellers/SellerProductPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products-and-services/:productSlug/variants/:variantSlug',
    component: lazy(() => import('pages/sellers/SellerCatalogDetailsPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products-and-services/:productSlug',
    component: lazy(() => import('pages/sellers/SellerCatalogDetailsPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/products-and-services',
    component: lazy(() => import('pages/sellers/SellerCatalogPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/profile',
    component: lazy(() => import('pages/sellers/SellerProfilePage')),
    permissionKey: 'account.profile',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/notifications',
    component: lazy(() => import('pages/sellers/SellerNotificationPage')),
    permissionKey: 'account.notifications',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/policies',
    component: lazy(() => import('pages/sellers/SellerPolicyPage')),
    permissionKey: 'account.policies',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/policies/:policyUid',
    component: lazy(() => import('pages/sellers/SellerPolicyDetailsPage')),
    permissionKey: 'account.policies',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/suggestions',
    component: lazy(() => import('pages/sellers/SellerProductSuggestionsPage')),
    permissionKey: 'catalog.suggestions',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/concierge/view/:conciergeCode',
    component: lazy(() => import('pages/sellers/SellerConciergeDetailPage')),
    permissionKey: 'cx.concierge',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/concierge',
    component: lazy(() => import('pages/sellers/SellerConciergePage')),
    permissionKey: 'cx.concierge',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/pay-by-links/:basketUid',
    component: lazy(() => import('pages/sellers/SellerPayByLinkDetailsPage')),
    permissionKey: 'shops.payment_links',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/pay-by-links',
    component: lazy(() => import('pages/sellers/SellerPayByLinkPage')),
    permissionKey: 'shops.payment_links',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/integrations',
    component: lazy(() => import('pages/sellers/SellerIntegrationPage')),
    permissionKey: 'integrations.integrations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/power-ups',
    component: lazy(() => import('pages/sellers/SellerPowerUpPage')),
    permissionKey: 'integrations.powerups',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/power-ups/olist-erp/authentication',
    component: lazy(() => import('pages/sellers/SellerPowerUpOlistErpAuthPage')),
    permissionKey: 'integrations.powerups',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/power-ups/:powerUpCode',
    component: lazy(() => import('pages/sellers/SellerPowerUpDetailsPage')),
    permissionKey: 'integrations.powerups',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/orders',
    component: lazy(() => import('pages/sellers/SellerOrdersPage')),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/orders/reports',
    component: lazy(() => import('pages/sellers/SellerOrdersReportsPage')),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/detailed-view',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/orders`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/indicators',
    component: lazy(() => import('pages/sellers/SellerSalesIndicatorsPage')),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/abandoned-carts',
    component: lazy(() => import('pages/sellers/SellerSalesAbandonedCartsPage')),
    permissionKey: 'shops.baskets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales/:orderNumber',
    component: lazy(() => import('pages/sellers/SellerSalesDetailsPage')),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/orders`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/orders/detailed-view',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/orders`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/orders/indicators',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/indicators`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/orders/abandoned-carts',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/abandoned-carts`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'shops.baskets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/orders/:orderNumber',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/${match.params.orderNumber}`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/orders',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/orders`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'orders.orders',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices/my-invoices',
    component: lazy(() => import('pages/sellers/SellerTaxInvoiceMyInvoicesPage')),
    permissionKey: 'tax_invoice.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices/commission-approvals',
    component: lazy(() => import('pages/sellers/SellerTaxInvoiceCommissionApprovalsPage')),
    permissionKey: 'financial.withdrawals',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/tax-invoices/my-invoices`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'tax_invoice.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices/:taxInvoiceUid',
    component: lazy(() => import('pages/sellers/SellerTaxInvoiceEditPage')),
    permissionKey: 'tax_invoice.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices/service/:taxInvoiceUid',
    component: lazy(() => import('pages/sellers/SellerTaxInvoiceServiceDetailsPage')),
    permissionKey: 'tax_invoice.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tax-invoices/product/:taxInvoiceUid',
    component: lazy(() => import('pages/sellers/SellerTaxInvoiceProductDetailsPage')),
    permissionKey: 'tax_invoice.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/options/:productOptionCode',
    component: lazy(() => import('pages/sellers/SellerProductOptionEditPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/options',
    component: lazy(() => import('pages/sellers/SellerProductOptionPage')),
    permissionKey: 'catalog.products',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/items',
    component: lazy(() => import('pages/sellers/SellerItemPage')),
    permissionKey: 'shops.allocations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/ranges',
    component: lazy(() => import('pages/sellers/SellerRangePage')),
    permissionKey: 'promotions.ranges',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/offers/view/:offerUid',
    component: lazy(() => import('pages/sellers/SellerOfferDetailsPage')),
    permissionKey: 'promotions.offers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/offers',
    component: lazy(() => import('pages/sellers/SellerOfferPage')),
    permissionKey: 'promotions.offers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/vouchers/view/:voucherUid',
    component: lazy(() => import('pages/sellers/SellerVoucherDetailsPage')),
    permissionKey: 'promotions.vouchers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/vouchers',
    component: lazy(() => import('pages/sellers/SellerVoucherPage')),
    permissionKey: 'promotions.vouchers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/voucher-sets/:voucherSetUid',
    component: lazy(() => import('pages/sellers/SellerVoucherSetDetailsPage')),
    permissionKey: 'promotions.vouchers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/voucher-sets',
    component: lazy(() => import('pages/sellers/SellerVoucherSetsPage')),
    permissionKey: 'promotions.vouchers',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tuition/:tuitionTemplateCode',
    component: lazy(() => import('pages/sellers/SellerTuitionTemplateDetailPage')),
    permissionKey: 'pricing.tuition_templates',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tuition',
    component: lazy(() => import('pages/sellers/SellerTuitionTemplatePage')),
    permissionKey: 'pricing.tuition_templates',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/contract/:contractTemplateUid',
    component: lazy(() => import('pages/sellers/SellerContractTemplateDetailsPage')),
    permissionKey: 'pricing.contract_templates',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/contract',
    component: lazy(() => import('pages/sellers/SellerContractTemplatePage')),
    permissionKey: 'pricing.contract_templates',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tasks/:taskCode',
    component: lazy(() => import('pages/sellers/SellerTaskDetailPage')),
    permissionKey: 'tasks.tasks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tasks',
    component: lazy(() => import('pages/sellers/SellerTaskPage')),
    permissionKey: 'tasks.tasks',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/groups/:groupUid',
    component: lazy(() => import('pages/sellers/SellerGroupDetailsPage')),
    permissionKey: 'permissions.groups',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/groups',
    component: lazy(() => import('pages/sellers/SellerGroupPage')),
    permissionKey: 'permissions.groups',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/users/:staffUid',
    component: lazy(() => import('pages/sellers/SellerUserDetailsPage')),
    permissionKey: 'permissions.users',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/users',
    component: lazy(() => import('pages/sellers/SellerUserPage')),
    permissionKey: 'permissions.users',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/tickets/:ticketId',
    component: lazy(() => import('pages/sellers/SellerSupportTicketDetailsPage')),
    permissionKey: 'support.can_view_tickets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/tickets',
    component: lazy(() => import('pages/sellers/SellerSupportTicketsPage')),
    permissionKey: 'support.can_view_tickets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/platform-tickets/:platformTicketId',
    component: lazy(() => import('pages/sellers/SellerSupportPlatformTicketDetailsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/platform-tickets',
    component: lazy(() => import('pages/sellers/SellerSupportPlatformTicketsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/platform-articles/:platformArticleSlug',
    component: lazy(() => import('pages/sellers/SellerSupportPlatformArticleDetailsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/platform-articles',
    component: lazy(() => import('pages/sellers/SellerSupportPlatformArticlesPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/indicators',
    component: lazy(() => import('pages/sellers/SellerSupportIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/support/configuration',
    component: lazy(() => import('pages/sellers/SellerSupportConfigurationPage')),
    permissionKey: 'support.configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/abandoned-carts',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/abandoned-carts`,
          search: location.search,
        }}
      />
    ),
    permissionKey: 'shops.baskets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/baskets',
    component: lazy(() => import('pages/sellers/SellerBasketsPage')),
    permissionKey: 'shops.baskets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/baskets/:basketUid',
    component: lazy(() => import('pages/sellers/SellerBasketDetailsPage')),
    permissionKey: 'shops.baskets',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/user-reviews',
    component: lazy(() => import('pages/sellers/SellerShopUserReviewsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/nps-score',
    component: lazy(() => import('pages/sellers/SellerShopUserReviewsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/featured',
    component: lazy(() => import('pages/sellers/SellerFeaturedPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/featured-template',
    component: lazy(() => import('pages/sellers/SellerFeaturedTemplatePage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sites/:subdomain',
    component: lazy(() => import('pages/sellers/SellerSiteDetailsPage')),
    permissionKey: 'shops.sites',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sites',
    component: lazy(() => import('pages/sellers/SellerSitePage')),
    permissionKey: 'shops.sites',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/channels/:channelUid',
    component: lazy(() => import('pages/sellers/SellerChannelDetailsPage')),
    permissionKey: 'shops.channels',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/channels',
    component: lazy(() => import('pages/sellers/SellerChannelPage')),
    permissionKey: 'shops.channels',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/channels-access-request',
    component: lazy(() => import('pages/sellers/SellerChannelAccessRequestPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/settings/tags',
    component: lazy(() => import('pages/sellers/SellerSettingsTagsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/tags',
    component: lazy(() => import('pages/sellers/SellerSettingsTagsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/orders',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales/indicators`,
          search: location.search,
        }}
      />
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/general',
    component: lazy(() => import('pages/sellers/SellerIndicatorsGeneralPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/financial',
    component: lazy(() => import('pages/sellers/SellerFinancialIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/payments',
    component: lazy(() => import('pages/sellers/SellerPaymentIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/eskolare-desk',
    component: lazy(() => import('pages/sellers/SellerSupportIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/indicators/sales-agreement',
    component: lazy(() => import('pages/sellers/SellerSalesAgreementIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/registration',
    component: lazy(() => import('pages/sellers/SellerSalesAgreementRegistrationPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/indicators',
    component: lazy(() => import('pages/sellers/SellerSalesAgreementIndicatorsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/commission-accounts/:commissionAccountUid',
    component: lazy(() =>
      import('pages/sellers/SellerSalesAgreementCommissionAccountsDetailsPage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/commission-accounts',
    component: lazy(() => import('pages/sellers/SellerSalesAgreementCommissionAccountsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/import',
    component: ({ match, location }) => {
      const params = new URLSearchParams(location.search);
      params.set('importacao', '1');
      return (
        <Redirect
          to={{
            pathname: `/sellers/${match.params.sellerSlug}/sales-agreements/registration`,
            search: `?${params}`,
            hash: location.hash,
          }}
        />
      );
    },
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements/:salesAgreementCode',
    component: lazy(() => import('pages/sellers/SellerSalesAgreementDetailsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sales-agreements',
    component: ({ match, location }) => (
      <Redirect
        to={{
          pathname: `/sellers/${match.params.sellerSlug}/sales-agreements/registration`,
          search: location.search,
        }}
      />
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/b2b-sales-portal/performance',
    component: lazy(() => import('pages/sellers/SellerB2bSalesPortalPerformancePage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/b2b-sales-portal/:salesAgreementCode',
    component: lazy(() => import('pages/sellers/SellerB2bSalesPortalDetailsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/b2b-sales-portal',
    component: lazy(() => import('pages/sellers/SellerB2bSalesPortalPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/plan/:billingPlanCode',
    component: lazy(() => import('pages/sellers/SellerBillingPlanDetailsPage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/plan',
    component: lazy(() => import('pages/sellers/SellerBillingPlanPage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/invoices/:invoiceCode',
    component: lazy(() => import('pages/sellers/SellerBillingInvoiceDetailsPage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/invoices',
    component: lazy(() => import('pages/sellers/SellerBillingInvoicePage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/tax-invoices',
    component: lazy(() => import('pages/sellers/SellerBillingTaxInvoicePage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/credit-cards',
    component: lazy(() => import('pages/sellers/SellerBillingCreditCardPage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/billing/bank-accounts',
    component: lazy(() => import('pages/sellers/SellerBillingBankAccountsPage')),
    permissionKey: 'billingmanager.billingmanager',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/initial-setup',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementInitialSetupPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-students',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementInstitutionStudentsPage')),
    permissionKey: 'school_management.can_view_students',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/enrollments',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementEnrollmentsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/enrollments/:institutionStudentEnrollmentUid',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementEnrollmentDetailsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/contract-payments',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementContractPaymentsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-contracts',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementInstitutionContractPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-contracts/:institutionContractUid',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractDetailsPage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-contract-groups',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractGroupPage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-contract-groups/create',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractGroupCreatePage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/institution-contract-groups/:institutionContractGroupUid',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractGroupDetailsPage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/billing-rule/:collectionNotificationUid',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementCollectionNotificationDetailsPage'),
    ),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/billing-rule',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementCollectionNotificationPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/performance',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementPerformancePage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/tuition-store/insights',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementTuitionStoreInsightsPage')),
    permissionKey: 'school_management.can_view_enrollment_store',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/tuition-store/showcases',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementTuitionStoreShowcasesPage')),
    permissionKey: 'school_management.can_view_enrollment_store',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/tuition-store/configurations',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementTuitionStoreConfigurationPage'),
    ),
    permissionKey: 'school_management.can_view_enrollment_store',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementConfigurationPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/courses',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementCoursesPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/courses/insights',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementCoursesInsightsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/courses/:courseUid',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementCoursesDetailsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/class-groups',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementClassGroupsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/class-groups/:classGroupUid',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementClassGroupsDetailsPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/institution-enrollment-templates',
    component: lazy(() => import('pages/sellers/SellerSchoolManagementEnrollmentTemplatesPage')),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/institution-contract-templates',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractTemplatePage'),
    ),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/school-management/configurations/institution-contract-templates/:institutionContractTemplateUid',
    component: lazy(() =>
      import('pages/sellers/SellerSchoolManagementInstitutionContractTemplateDetailsPage'),
    ),
    permissionKey: 'school_management.can_view_configurations',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/points',
    component: lazy(() => import('pages/sellers/SellerPointsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/points/partner-referral',
    component: lazy(() => import('pages/sellers/SellerPointsPartnerReferralPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/points/catalog-items',
    component: lazy(() => import('pages/sellers/SellerPointsCatalogItemsPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/points/regulations',
    component: lazy(() => import('pages/sellers/SellerPointsRegulationPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/points/rules',
    component: lazy(() => import('pages/sellers/SellerPointsRulesPage')),
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/forms',
    component: lazy(() => import('pages/sellers/SellerFormPage')),
    permissionKey: 'forms.can_view_forms',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/forms/:formUid',
    component: lazy(() => import('pages/sellers/SellerFormDetailsPage')),
    permissionKey: 'forms.can_view_forms',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sign/envelopes/:envelopeCode',
    component: lazy(() => import('pages/sellers/SellerSignEnvelopeDetailsPage')),
    permissionKey: 'sign.sign',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sign/envelopes',
    component: lazy(() => import('pages/sellers/SellerSignEnvelopePage')),
    permissionKey: 'sign.sign',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sign/envelope-templates/:envelopeTemplateCode',
    component: lazy(() => import('pages/sellers/SellerSignEnvelopeTemplateDetailsPage')),
    permissionKey: 'sign.sign',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug/sign/envelope-templates',
    component: lazy(() => import('pages/sellers/SellerSignEnvelopeTemplatePage')),
    permissionKey: 'sign.sign',
  },
  {
    exact: true,
    path: '/sellers/:sellerSlug',
    component: lazy(() => import('pages/sellers/SellerHomePage')),
  },
];
